import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { Toaster } from "@/components/ui/sonner";
import { ProtectedRoute, PublicOnlyRoute } from "@/components/RouteGuards";
import Splash from "@/components/Splash";

// Eagerly loaded (small, first-paint critical)
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import HomePage from "@/pages/HomePage";
import AuthCallback from "@/pages/AuthCallback";

// Route-based code splitting for larger, less-hot screens
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const TablesPage = lazy(() => import("@/pages/TablesPage"));
const FriendsPage = lazy(() => import("@/pages/FriendsPage"));
const LeaderboardPage = lazy(() => import("@/pages/LeaderboardPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const PhysicalGameCreatePage = lazy(() => import("@/pages/PhysicalGameCreatePage"));
const PhysicalGamePlayPage = lazy(() => import("@/pages/PhysicalGamePlayPage"));
const MatchHistoryPage = lazy(() => import("@/pages/MatchHistoryPage"));
const GroupCreatePage = lazy(() => import("@/pages/GroupCreatePage"));
const GroupDetailPage = lazy(() => import("@/pages/GroupDetailPage"));
const OnlineRoomCreatePage = lazy(() => import("@/pages/OnlineRoomCreatePage"));
const JoinRoomPage = lazy(() => import("@/pages/JoinRoomPage"));
const WaitingRoomPage = lazy(() => import("@/pages/WaitingRoomPage"));
const LiveGamePage = lazy(() => import("@/pages/LiveGamePage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const WrappedPage = lazy(() => import("@/pages/WrappedPage"));
const SeasonDetailPage = lazy(() => import("@/pages/SeasonDetailPage"));

import "@/App.css";

function OAuthHashInterceptor({ children }) {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return children;
}

function SplashGate({ children }) {
  const { user } = useAuth();
  const [minElapsed, setMinElapsed] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMinElapsed(true), 900);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (minElapsed && user !== null) {
      const el = document.getElementById("boot-splash");
      if (el) el.remove();
      const t = setTimeout(() => setHidden(true), 150);
      return () => clearTimeout(t);
    }
  }, [minElapsed, user]);

  return (
    <>
      <Splash show={!hidden} />
      {children}
    </>
  );
}

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-8 w-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <SplashGate>
          <OAuthHashInterceptor>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/karsilama" element={<PublicOnlyRoute><LandingPage /></PublicOnlyRoute>} />
            <Route path="/giris" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
            <Route path="/kayit" element={<PublicOnlyRoute><RegisterPage /></PublicOnlyRoute>} />
            <Route path="/sifremi-unuttum" element={<PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>} />
            <Route path="/oauth-callback" element={<AuthCallback />} />

            <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
            <Route path="/masalar" element={<ProtectedRoute><TablesPage /></ProtectedRoute>} />
            <Route path="/arkadaslar" element={<ProtectedRoute><FriendsPage /></ProtectedRoute>} />
            <Route path="/siralama" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
            <Route path="/profil" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/bildirimler" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

            <Route path="/fiziksel/yeni" element={<ProtectedRoute><PhysicalGameCreatePage /></ProtectedRoute>} />
            <Route path="/fiziksel/:id" element={<ProtectedRoute><PhysicalGamePlayPage /></ProtectedRoute>} />
            <Route path="/mac-gecmisi" element={<ProtectedRoute><MatchHistoryPage /></ProtectedRoute>} />

            <Route path="/gruplar/yeni" element={<ProtectedRoute><GroupCreatePage /></ProtectedRoute>} />
            <Route path="/gruplar/:id" element={<ProtectedRoute><GroupDetailPage /></ProtectedRoute>} />

            <Route path="/online/yeni" element={<ProtectedRoute><OnlineRoomCreatePage /></ProtectedRoute>} />
            <Route path="/online/katil" element={<ProtectedRoute><JoinRoomPage /></ProtectedRoute>} />
            <Route path="/oda/:code" element={<ProtectedRoute><WaitingRoomPage /></ProtectedRoute>} />
            <Route path="/oda/:code/oyun" element={<ProtectedRoute><LiveGamePage /></ProtectedRoute>} />
            <Route path="/kod/:code" element={<ProtectedRoute><WaitingRoomPage /></ProtectedRoute>} />
            <Route path="/yonetim" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
            <Route path="/wrapped" element={<ProtectedRoute><WrappedPage /></ProtectedRoute>} />
            <Route path="/gruplar/:id/sezon/:seasonId" element={<ProtectedRoute><SeasonDetailPage /></ProtectedRoute>} />

            <Route path="*" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
          </Routes>
          </Suspense>
          </OAuthHashInterceptor>
        </SplashGate>
        <Toaster position="top-center" richColors />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
